import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaTimes, FaYoutube, FaInstagram } from 'react-icons/fa';
import channelLogo from '../assets/channels4_profile.jpg';

const Header = ({ YOUTUBE_CHANNEL, INSTAGRAM_PROFILE }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Me' },
    { path: '/services', label: 'Services' },
    { path: '/calorie-calculator', label: '🔥 Calories' },
    { path: '/workout-plans', label: 'Workout Plans' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/community', label: 'Community' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-black border-b-4 border-yellow-500 sticky top-0 z-50 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between py-4">
          {/* Logo & Brand */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src={channelLogo}
              alt="Rugged Fitness Coach Sabari"
              className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-yellow-500 group-hover:border-orange-500 transition-all"
            />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-yellow-400 uppercase tracking-wide">
                Coach Sabari
              </h1>
              <p className="text-xs text-gray-400">Rugged Fitness Trainer</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-bold uppercase text-sm tracking-wide transition-all ${
                  isActive(link.path)
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-black'
                    : 'text-gray-300 hover:text-yellow-400 hover:bg-gray-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Social Links & Mobile Menu */}
          <div className="flex items-center space-x-4">
            {/* Social Icons */}
            <div className="hidden md:flex items-center space-x-3">
              <a
                href={YOUTUBE_CHANNEL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-500 hover:text-red-400 transition-colors"
                title="YouTube"
              >
                <FaYoutube size={24} />
              </a>
              <a
                href={INSTAGRAM_PROFILE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-500 hover:text-pink-400 transition-colors"
                title="Instagram"
              >
                <FaInstagram size={24} />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-yellow-400 hover:text-orange-500 transition-colors p-2"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <FaTimes size={28} className="text-yellow-400" />
              ) : (
                <div className="flex flex-col space-y-1.5">
                  <span className="block w-8 h-1 bg-yellow-400 rounded"></span>
                  <span className="block w-8 h-1 bg-yellow-400 rounded"></span>
                  <span className="block w-8 h-1 bg-yellow-400 rounded"></span>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[88px] bg-black z-40 overflow-y-auto">
            <div className="px-4 py-6">
              <nav className="flex flex-col space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-3 text-base transition-all ${
                      isActive(link.path)
                        ? 'text-yellow-400 font-semibold'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              {/* Services Section */}
              <div className="mt-8 mb-6">
                <h3 className="text-orange-500 font-bold text-lg uppercase mb-4">Services</h3>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li>• Personal Training</li>
                  <li>• Online Coaching</li>
                  <li>• Nutrition Planning</li>
                  <li>• Workout Programs</li>
                  <li>• BMI Analysis</li>
                </ul>
              </div>

              {/* Contact Section */}
              <div className="mb-8">
                <h3 className="text-red-500 font-bold text-lg uppercase mb-4">Contact</h3>
                <ul className="space-y-3 text-gray-400 text-sm">
                  <li className="flex items-center space-x-2">
                    <span className="text-yellow-400">✉</span>
                    <span>sabariprimefit@gmail.com</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-orange-400">📞</span>
                    <span>+91 6383767287</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-red-400">📍</span>
                    <span>Tamil Nadu, India</span>
                  </li>
                </ul>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400">380+</div>
                  <div className="text-xs text-gray-500 uppercase">Subscribers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-400">80+</div>
                  <div className="text-xs text-gray-500 uppercase">Videos</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400">94K+</div>
                  <div className="text-xs text-gray-500 uppercase">Views</div>
                </div>
              </div>

              {/* Footer Text */}
              <div className="text-center text-gray-600 text-xs border-t border-gray-800 pt-4">
                <p className="mb-1">© {new Date().getFullYear()} Rugged Fitness Coach Sabari. All rights reserved.</p>
                <p className="flex items-center justify-center space-x-1">
                  <span>Made with</span>
                  <span className="text-red-500">❤</span>
                  <span>for Fitness Enthusiasts</span>
                </p>
              </div>

              {/* Social Links */}
              <div className="flex items-center justify-center space-x-6 mt-6">
                <a
                  href={YOUTUBE_CHANNEL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-500 hover:text-red-400 transition-colors"
                >
                  <FaYoutube size={32} />
                </a>
                <a
                  href={INSTAGRAM_PROFILE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-500 hover:text-pink-400 transition-colors"
                >
                  <FaInstagram size={32} />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
